import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { Store, BarChart3, Settings, QrCode, Image, Wallet, Tag } from 'lucide-react';
import Button from '../components/Button';
import { useMerchant } from '../hooks/useMerchant';
import {
    usePromotions,
    useMerchantStats,
    useMerchantRecentSales,
    useMerchantPlatformPayments,
    ESTABLISHMENT_DATA_CHANGED,
} from '../hooks/useData';
import { supabase } from '../lib/supabase';
import './MerchantPortal.css';
import { useToast } from '../contexts/ToastContext';
import Modal from '../components/Modal';
import {
    MerchantDashboardTab,
    MerchantFinanceTab,
    MerchantMediaTab,
    MerchantPixQrModal,
    MerchantPlatformFeeDetailsModal,
    MerchantPromotionModal,
    MerchantPromotionsTab,
    MerchantSettingsTab,
    MerchantValidateTab,
    type MerchantTabId,
    type NewPromotion,
    type PixPaymentPayload,
    type ValidationResult,
} from './merchantPortal/MerchantPortalTabs';

/** Conteúdo do QR: `TURISTA:<establishmentId>:<code>` (alinhado a Benefits e fallback em Establishment). */
function parseTouristCouponQr(decoded: string): { establishmentId: string; code: string } | null {
    const prefix = 'TURISTA:';
    const trimmed = decoded.trim();
    if (!trimmed.startsWith(prefix)) return null;
    const rest = trimmed.slice(prefix.length);
    const i = rest.indexOf(':');
    if (i <= 0 || i >= rest.length - 1) return null;
    return {
        establishmentId: rest.slice(0, i).trim(),
        code: rest.slice(i + 1).trim().toUpperCase().replace(/\s+/g, ''),
    };
}

function parsePositivePurchase(raw: string): number | null {
    const n = parseFloat(String(raw).replace(',', '.').trim());
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
}

const MerchantPortal = () => {
    const navigate = useNavigate();
    const { merchant, login, logout, isAuthenticated, isLoading } = useMerchant();
    const [activeTab, setActiveTab] = useState<MerchantTabId>('dashboard');
    const [showNewPromo, setShowNewPromo] = useState(false);
    const [newPromo, setNewPromo] = useState<NewPromotion>({
        title: '',
        discountMode: 'levels',
        customDiscountName: '',
        customDiscountPercent: 10,
        customVisibility: 'public',
        exclusiveCouponCode: '',
        discountBronze: 5,
        discountSilver: 6,
        discountGold: 8,
        maxUses: null,
        maxUsesPerUser: null,
        endDate: '',
    });

    const [couponCode, setCouponCode] = useState('');
    const [validationPurchaseAmount, setValidationPurchaseAmount] = useState('');
    const validationPurchaseAmountRef = useRef('');
    const [validating, setValidating] = useState(false);
    const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
    const [validationError, setValidationError] = useState('');

    const [scannerActive, setScannerActive] = useState(false);
    const html5QrCodeRef = useRef<Html5Qrcode | null>(null);

    const establishmentId = merchant?.establishmentId || '';
    const [promotionsRefreshKey, setPromotionsRefreshKey] = useState(0);
    const { data: promotions, loading: promoLoading } = usePromotions(establishmentId, undefined, promotionsRefreshKey);
    const { data: stats } = useMerchantStats(establishmentId);
    const { data: recentSales } = useMerchantRecentSales(establishmentId);
    const {
        summary: feeSummary,
        payments: platformPayments,
        loading: feeLoading,
        refresh: refreshPlatformPayments,
    } = useMerchantPlatformPayments(establishmentId);
    const [creatingPayment, setCreatingPayment] = useState(false);
    const [pixPayment, setPixPayment] = useState<PixPaymentPayload | null>(null);
    const [pixModalOpen, setPixModalOpen] = useState(false);
    const [feeDetailsOpen, setFeeDetailsOpen] = useState(false);
    const [pixCopied, setPixCopied] = useState(false);

    const toast = useToast();
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [promoToDelete, setPromoToDelete] = useState<string | null>(null);

    const [gallery, setGallery] = useState<string[]>(merchant?.establishment?.gallery || []);
    const [video, setVideo] = useState<string>(merchant?.establishment?.video || '');
    const [coverPhoto, setCoverPhoto] = useState<string>(merchant?.establishment?.image || '');
    const [uploading, setUploading] = useState(false);
    const [locationData, setLocationData] = useState({ address: '', latitude: 0, longitude: 0 });
    const [updatingLocation, setUpdatingLocation] = useState(false);
    const [establishmentNameDraft, setEstablishmentNameDraft] = useState('');
    const [nameChangePassword, setNameChangePassword] = useState('');
    const [showNameChangePassword, setShowNameChangePassword] = useState(false);
    const [updatingName, setUpdatingName] = useState(false);
    const [establishmentDescriptionDraft, setEstablishmentDescriptionDraft] = useState('');
    const [updatingDescription, setUpdatingDescription] = useState(false);

    useEffect(() => {
        if (merchant?.establishment) {
            setGallery(merchant?.establishment.gallery || []);
            setVideo(merchant?.establishment.video || '');
            setCoverPhoto(merchant?.establishment.image || '');
            setLocationData({
                address: merchant?.establishment.address || '',
                latitude: merchant?.establishment.latitude || 0,
                longitude: merchant?.establishment.longitude || 0
            });
            setEstablishmentNameDraft(merchant.establishment.name || '');
            // Não sobrescrever com '' quando o RPC de login não traz `description` (campo sumia no formulário).
            if (merchant.establishment.description !== undefined) {
                setEstablishmentDescriptionDraft(merchant.establishment.description ?? '');
            }
        }
    }, [merchant]);

    useEffect(() => {
        if (!establishmentId) return;
        let cancelled = false;
        (async () => {
            const { data, error } = await supabase
                .from('Establishment')
                .select('description')
                .eq('id', establishmentId)
                .maybeSingle();
            if (cancelled) return;
            if (error) return;
            const raw = data?.description;
            setEstablishmentDescriptionDraft(raw == null ? '' : String(raw));
        })();
        return () => {
            cancelled = true;
        };
    }, [establishmentId]);

    const getImageUrl = (url: string) => {
        if (!url) return '';
        if (url.startsWith('http')) return url;
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        return `${supabaseUrl}/storage/v1/object/public/media/${url}`;
    };

    const handleUpdateLocation = async () => {
        if (!merchant) return;
        const establishmentRowId =
            merchant.establishmentId ||
            merchant.establishment?.id ||
            (merchant as unknown as { establishment_id?: string }).establishment_id;
        if (!establishmentRowId) {
            toast.error('Estabelecimento não identificado. Entre novamente na conta.');
            return;
        }
        setUpdatingLocation(true);
        try {
            // PATCH direto em Establishment costuma falhar com cliente anon + RLS (0 linhas / 406 com .maybeSingle).
            const { data: rpcRows, error } = await supabase.rpc('update_establishment_location', {
                p_establishment_id: establishmentRowId,
                p_address: locationData.address,
                p_latitude: locationData.latitude,
                p_longitude: locationData.longitude,
            });
            if (error) throw error;
            const updated = Array.isArray(rpcRows) ? rpcRows[0] : rpcRows;
            if (!updated) {
                console.error(
                    '[MerchantPortal] RPC update_establishment_location não alterou linhas (id inválido ou RPC não criada no Supabase).',
                    { establishmentRowId },
                );
                toast.error('Não foi possível salvar a localização agora. Tente novamente ou fale com o suporte.');
                return;
            }
            login({
                ...merchant,
                establishmentId: establishmentRowId,
                establishment: {
                    ...merchant.establishment,
                    address: updated.address ?? locationData.address,
                    latitude: updated.latitude ?? locationData.latitude,
                    longitude: updated.longitude ?? locationData.longitude,
                },
            });
            toast.success('Localização atualizada com sucesso!');
        } catch (e: unknown) {
            const err = e as { code?: string; message?: string };
            const msg = (err?.message ?? '').toLowerCase();
            const missingRpc =
                err?.code === 'PGRST202' ||
                msg.includes('could not find the function') ||
                msg.includes('schema cache') ||
                msg.includes('404');
            if (missingRpc) {
                console.error('[MerchantPortal] Configuração ausente para atualizar localização.', err);
                toast.error('Não foi possível salvar a localização agora. Tente novamente ou fale com o suporte.');
            } else {
                toast.error(err?.message?.trim() || 'Erro ao atualizar localização');
            }
        } finally {
            setUpdatingLocation(false);
        }
    };

    const handleUpdateEstablishmentName = async () => {
        if (!merchant) return;
        const establishmentRowId =
            merchant.establishmentId ||
            merchant.establishment?.id ||
            (merchant as unknown as { establishment_id?: string }).establishment_id;
        if (!establishmentRowId) {
            toast.error('Estabelecimento não identificado. Entre novamente na conta.');
            return;
        }
        const trimmed = establishmentNameDraft.trim();
        if (!trimmed) {
            toast.error('Informe um nome para o estabelecimento.');
            return;
        }
        if (!nameChangePassword) {
            toast.error('Digite sua senha para confirmar a alteração do nome.');
            return;
        }
        setUpdatingName(true);
        try {
            const { data: rpcRows, error } = await supabase.rpc('update_establishment_name', {
                p_establishment_id: String(establishmentRowId).trim(),
                p_merchant_email: merchant.email.trim(),
                p_password: nameChangePassword.trim(),
                p_new_name: trimmed,
            });
            if (error) throw error;
            const rows = Array.isArray(rpcRows) ? rpcRows : rpcRows != null ? [rpcRows] : [];
            const updated = rows[0] as { id?: string; name?: string } | undefined;
            if (!updated?.id) {
                toast.error('Não foi possível confirmar a senha ou salvar o novo nome. Confira os dados e tente novamente.');
                return;
            }

            const idNorm = String(establishmentRowId).trim();
            const { data: freshRow, error: freshErr } = await supabase
                .from('Establishment')
                .select('name')
                .eq('id', idNorm)
                .maybeSingle();

            const nameFromDb =
                !freshErr && freshRow && typeof freshRow.name === 'string' && freshRow.name.trim() !== ''
                    ? freshRow.name.trim()
                    : typeof updated.name === 'string'
                      ? updated.name.trim()
                      : trimmed;

            setNameChangePassword('');
            setShowNameChangePassword(false);
            login({
                ...merchant,
                establishmentId: idNorm,
                establishment: {
                    ...merchant.establishment,
                    id: merchant.establishment?.id ?? idNorm,
                    name: nameFromDb,
                },
            });

            window.dispatchEvent(
                new CustomEvent(ESTABLISHMENT_DATA_CHANGED, { detail: { id: idNorm } }),
            );

            toast.success('Nome do estabelecimento atualizado.');
        } catch (e: unknown) {
            const err = e as { code?: string; message?: string };
            const msg = (err?.message ?? '').toLowerCase();
            const missingRpc =
                err?.code === 'PGRST202' ||
                msg.includes('could not find the function') ||
                msg.includes('schema cache') ||
                msg.includes('404');
            if (missingRpc) {
                console.error('[MerchantPortal] Configuração ausente para atualizar nome.', err);
                toast.error('Não foi possível salvar o nome agora. Tente novamente ou fale com o suporte.');
            } else {
                toast.error(err?.message?.trim() || 'Erro ao atualizar o nome.');
            }
        } finally {
            setUpdatingName(false);
        }
    };

    const handleUpdateEstablishmentDescription = async () => {
        if (!merchant) return;
        const establishmentRowId =
            merchant.establishmentId ||
            merchant.establishment?.id ||
            (merchant as unknown as { establishment_id?: string }).establishment_id;
        if (!establishmentRowId) {
            toast.error('Estabelecimento não identificado. Entre novamente na conta.');
            return;
        }
        const idNorm = String(establishmentRowId).trim();
        const text = establishmentDescriptionDraft.trim();
        setUpdatingDescription(true);
        try {
            const { data: rpcRows, error } = await supabase.rpc('update_establishment_description', {
                p_establishment_id: idNorm,
                p_description: text,
            });
            if (error) throw error;
            const updated = Array.isArray(rpcRows) ? rpcRows[0] : rpcRows;
            if (!updated) {
                toast.error('Não foi possível salvar a descrição agora. Tente novamente ou fale com o suporte.');
                return;
            }
            // Texto que o lojista acabou de salvar (evita coluna `about` antiga no retorno da RPC mascarar o draft).
            const desc = text;
            login({
                ...merchant,
                establishmentId: idNorm,
                establishment: {
                    ...merchant.establishment,
                    id: merchant.establishment?.id ?? idNorm,
                    description: desc,
                },
            });
            setEstablishmentDescriptionDraft(desc);
            window.dispatchEvent(
                new CustomEvent(ESTABLISHMENT_DATA_CHANGED, { detail: { id: idNorm } }),
            );
            toast.success('Descrição atualizada. Os turistas verão na ficha do local.');
        } catch (e: unknown) {
            const err = e as { code?: string; message?: string };
            const msg = (err?.message ?? '').toLowerCase();
            const missingRpc =
                err?.code === 'PGRST202' ||
                msg.includes('could not find the function') ||
                msg.includes('schema cache') ||
                msg.includes('404');
            if (missingRpc) {
                console.error('[MerchantPortal] Configuração ausente para atualizar descrição.', err);
                toast.error('Não foi possível salvar a descrição agora. Tente novamente ou fale com o suporte.');
            } else {
                toast.error(err?.message?.trim() || 'Erro ao salvar a descrição.');
            }
        } finally {
            setUpdatingDescription(false);
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'video' | 'cover') => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = '';

        setUploading(true);
        try {
            const ext = file.name.split('.').pop();
            const path = `${establishmentId}/${Date.now()}.${ext}`;
            const { error: uploadError } = await supabase.storage.from('media').upload(path, file);
            if (uploadError) {
                console.error('[MerchantPortal] Erro no upload storage:', uploadError);
                throw uploadError;
            }

            const { data: { publicUrl } } = supabase.storage.from('media').getPublicUrl(path);

            if (type === 'image') setGallery(prev => [...prev, publicUrl]);
            else if (type === 'video') setVideo(publicUrl);
            else if (type === 'cover') setCoverPhoto(publicUrl);
            toast.success('Upload concluído! Clique em "Salvar Alterações" para aplicar.');
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Erro ao fazer upload da mídia';
            console.error('[MerchantPortal] Falha no upload:', err);
            toast.error(msg);
        } finally {
            setUploading(false);
        }
    };

    const handleSaveMedia = async () => {
        if (!merchant) return;
        try {
            const { error } = await supabase.rpc('update_establishment_media', {
                p_establishment_id: merchant.establishmentId,
                p_image: coverPhoto || '',
                p_gallery: gallery,
                p_video: video || null,
            });
            if (error) {
                console.error('[MerchantPortal] Erro ao atualizar Establishment (RPC):', error);
                throw error;
            }
            login({
                ...merchant,
                establishment: { ...merchant.establishment, image: coverPhoto, gallery, video },
            });
            toast.success('Mídia atualizada com sucesso! A imagem de capa já aparece para os usuários.');
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Erro ao salvar alterações';
            console.error('[MerchantPortal] Falha ao salvar mídia:', err);
            toast.error(msg);
        }
    };

    const removeImage = (index: number) => {
        const newGallery = [...gallery];
        newGallery.splice(index, 1);
        setGallery(newGallery);
    };

    const runValidateRedemption = useCallback(async (code: string, purchaseAmount: number) => {
        if (!merchant) return;
        const normalized = code.trim().toUpperCase().replace(/\s+/g, '');
        if (!normalized) return;
        if (!Number.isFinite(purchaseAmount) || purchaseAmount <= 0) {
            toast.warning('Informe o valor total da compra antes de validar.');
            return;
        }
        const eidMerchant = String(merchant.establishmentId).trim();
        setValidating(true);
        setValidationResult(null);
        setValidationError('');
        try {
            const { data, error } = await supabase.rpc('validate_redemption', {
                p_code: normalized,
                p_establishment_id: eidMerchant,
                p_purchase_amount: purchaseAmount,
            });
            if (error) throw new Error(error.message);
            const payload = data as { success?: boolean; message?: string };
            if (payload && payload.success === false) {
                throw new Error(typeof payload.message === 'string' ? payload.message : 'Cupom inválido');
            }
            setValidationResult(data as ValidationResult);
            setCouponCode('');
            setValidationPurchaseAmount('');
            validationPurchaseAmountRef.current = '';
            toast.success('Cupom validado com sucesso!');
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Cupom inválido';
            setValidationError(msg);
            toast.error(msg);
        } finally {
            setValidating(false);
        }
    }, [merchant, toast]);

    useEffect(() => {
        if (!isLoading && !isAuthenticated) navigate('/merchant-login');
    }, [isLoading, isAuthenticated, navigate]);

    useEffect(() => {
        return () => { if (html5QrCodeRef.current) html5QrCodeRef.current.stop().catch(() => {}); };
    }, []);

    useEffect(() => {
        if (scannerActive && !html5QrCodeRef.current) {
            const width = window.innerWidth;
            const size = Math.min(width - 40, 250);
            const html5QrCode = new Html5Qrcode('qr-reader');
            html5QrCodeRef.current = html5QrCode;
            html5QrCode.start(
                { facingMode: 'environment' },
                { fps: 10, qrbox: { width: size, height: size }, aspectRatio: 1 },
                async (decodedText) => {
                    if (html5QrCodeRef.current) {
                        try { if (html5QrCodeRef.current.isScanning) await html5QrCodeRef.current.stop(); html5QrCodeRef.current.clear(); } catch { void 0; }
                        html5QrCodeRef.current = null;
                    }
                    setScannerActive(false);
                    const parsed = parseTouristCouponQr(decodedText);
                    if (!parsed) {
                        toast.error('QR Code inválido. Use um cupom Facilita Pass+');
                        return;
                    }
                    if (!merchant) {
                        setCouponCode(parsed.code);
                        return;
                    }
                    const code = parsed.code;
                    const eidMerchant = String(merchant.establishmentId).trim();
                    if (parsed.establishmentId && eidMerchant && parsed.establishmentId !== eidMerchant) {
                        toast.error('Este cupom é de outro estabelecimento.');
                        return;
                    }
                    if (!code) {
                        toast.error('QR Code inválido. Use um cupom Facilita Pass+');
                        return;
                    }
                    const purchaseAmount = parsePositivePurchase(validationPurchaseAmountRef.current);
                    if (purchaseAmount == null) {
                        toast.warning('Informe o valor total da compra antes de escanear o QR Code.');
                        return;
                    }
                    await runValidateRedemption(code, purchaseAmount);
                },
                () => {}
            ).catch(() => { toast.error('Não foi possível acessar a câmera.'); setScannerActive(false); });
        }
    }, [scannerActive, toast, merchant, runValidateRedemption]);

    const handlePayPlatformFee = useCallback(async () => {
        if (!merchant) return;
        if (!feeSummary || feeSummary.pending <= 0) {
            toast.info('Sem taxa pendente para pagar no momento.');
            return;
        }
        setCreatingPayment(true);
        setPixCopied(false);
        try {
            const { data, error } = await supabase.functions.invoke('mercadopago-create-pix-payment', { body: {} });
            if (error) throw new Error(error.message);
            const payload = data as PixPaymentPayload | null;
            if (!payload?.qrCode) {
                throw new Error('Mercado Pago não devolveu o QR Code Pix.');
            }
            setPixPayment(payload);
            setFeeDetailsOpen(false);
            setPixModalOpen(true);
            toast.success('Pix gerado. Conclua o pagamento pelo QR Code ou copia e cola.');
            refreshPlatformPayments();
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Erro ao iniciar pagamento.';
            toast.error(msg);
        } finally {
            setCreatingPayment(false);
        }
    }, [merchant, feeSummary, toast, refreshPlatformPayments]);

    const copyPixCode = async () => {
        if (!pixPayment?.qrCode) return;
        try {
            await navigator.clipboard.writeText(pixPayment.qrCode);
            setPixCopied(true);
            setTimeout(() => setPixCopied(false), 2000);
            toast.success('Código Pix copiado.');
        } catch {
            toast.error('Não foi possível copiar o código Pix.');
        }
    };

    const checkPixPaymentStatus = useCallback(async () => {
        if (!pixPayment || !['pending', 'in_process'].includes(pixPayment.status)) return;

        try {
            const { data, error } = await supabase.functions.invoke('mercadopago-check-pix-payment', {
                body: { paymentId: pixPayment.paymentId },
            });
            if (error) throw new Error(error.message);

            const payload = data as Partial<PixPaymentPayload> | null;
            if (!payload?.status) return;

            setPixPayment((current) => {
                if (!current || current.paymentId !== pixPayment.paymentId) return current;
                return {
                    ...current,
                    status: payload.status ?? current.status,
                    mpOrderId: payload.mpOrderId ?? current.mpOrderId,
                    mpPaymentId: payload.mpPaymentId ?? current.mpPaymentId,
                    expiresAt: payload.expiresAt ?? current.expiresAt,
                    qrCode: payload.qrCode ?? current.qrCode,
                    qrCodeBase64: payload.qrCodeBase64 ?? current.qrCodeBase64,
                    ticketUrl: payload.ticketUrl ?? current.ticketUrl,
                };
            });

            if (payload.status === 'approved' && pixPayment.status !== 'approved') {
                toast.success('Pagamento Pix confirmado.');
            }
            refreshPlatformPayments();
        } catch (err) {
            if (import.meta.env.DEV) console.warn('[mercadopago-check-pix-payment]', err);
        }
    }, [pixPayment, refreshPlatformPayments, toast]);

    useEffect(() => {
        if (!pixModalOpen || !pixPayment || !['pending', 'in_process'].includes(pixPayment.status)) return;
        void checkPixPaymentStatus();
        const intervalId = window.setInterval(() => {
            void checkPixPaymentStatus();
        }, 12000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [checkPixPaymentStatus, pixModalOpen, pixPayment]);

    useEffect(() => {
        if (!pixPayment) return;
        const latest = platformPayments.find((payment) => payment.id === pixPayment.paymentId);
        if (!latest || latest.status === pixPayment.status) return;

        setPixPayment((current) => (
            current?.paymentId === latest.id
                ? { ...current, status: latest.status, expiresAt: latest.expiresAt ?? current.expiresAt }
                : current
        ));

        if (latest.status === 'approved') {
            toast.success('Pagamento Pix confirmado.');
        }
    }, [platformPayments, pixPayment, toast]);

    if (isLoading) {
        return (
            <div className="merchant-portal merchant-portal--loading" role="status" aria-live="polite">
                <div className="merchant-loading-inner">
                    <div className="merchant-loading-spinner" aria-hidden />
                    <p>Carregando portal...</p>
                </div>
            </div>
        );
    }
    if (!merchant) return null;

    const establishment = merchant.establishment;

    const handleLogout = () => { void logout(); navigate('/merchant-login'); };

    const startScanner = () => { setValidationError(''); setValidationResult(null); setScannerActive(true); };
    const stopScanner = async () => {
        if (html5QrCodeRef.current) {
            try { if (html5QrCodeRef.current.isScanning) await html5QrCodeRef.current.stop(); html5QrCodeRef.current.clear(); } catch { void 0; }
            html5QrCodeRef.current = null;
        }
        setScannerActive(false);
    };

    const handleValidateCoupon = async (codeToValidate?: string) => {
        const code = codeToValidate || couponCode.trim().toUpperCase();
        if (!code) { toast.warning('Digite um código para validar'); return; }
        const purchaseAmount = parsePositivePurchase(validationPurchaseAmount);
        if (purchaseAmount == null) {
            toast.warning('Informe o valor total da compra para validar o cupom.');
            return;
        }
        await runValidateRedemption(code, purchaseAmount);
    };

    const handleCreatePromo = async () => {
        if (!newPromo.title.trim()) {
            toast.warning('Dê um título à promoção (aparece para você na lista e para o turista no app).');
            return;
        }
        if (!newPromo.endDate?.trim()) {
            toast.warning('Escolha a data de término: até quando o cupom pode ser resgatado.');
            return;
        }
        if (newPromo.maxUses == null || !Number.isFinite(newPromo.maxUses) || newPromo.maxUses < 1) {
            toast.warning('Informe o máximo de usos total: quantos cupons podem ser resgatados no geral (mínimo 1).');
            return;
        }
        if (
            newPromo.maxUsesPerUser == null ||
            !Number.isFinite(newPromo.maxUsesPerUser) ||
            newPromo.maxUsesPerUser < 1
        ) {
            toast.warning(
                'Informe o máximo de usos por cliente: quantas vezes a mesma pessoa pode resgatar esta promoção (mínimo 1).',
            );
            return;
        }
        const maxUsesTotal = newPromo.maxUses;
        const maxUsesPerUser = newPromo.maxUsesPerUser;
        if (newPromo.discountMode === 'custom') {
            const p = Number(newPromo.customDiscountPercent);
            if (!Number.isFinite(p) || p < 1 || p > 100) {
                toast.warning('Informe um percentual entre 1% e 100% para calcular a economia no cupom.');
                return;
            }
            if (newPromo.customVisibility === 'secret') {
                const secret = newPromo.exclusiveCouponCode.trim();
                if (!secret) {
                    toast.warning(
                        'Defina o código secreto do cupom: o turista usa esse código em "Resgatar código do cupom" na página do estabelecimento.',
                    );
                    return;
                }
                if (secret.length < 2) {
                    toast.warning('O código secreto do cupom deve ter pelo menos 2 caracteres.');
                    return;
                }
            }
        }
        try {
            const useLevels = newPromo.discountMode === 'levels';
            const exclusiveCode =
                !useLevels && newPromo.customVisibility === 'secret'
                    ? newPromo.exclusiveCouponCode.trim()
                    : null;
            const customNameForApp =
                newPromo.customDiscountName.trim() || newPromo.title.trim();
            const { error } = await supabase.rpc('insert_promotion', {
                p_establishment_id: merchant.establishmentId,
                p_title: newPromo.title.trim(),
                p_discount_bronze: useLevels ? newPromo.discountBronze : null,
                p_discount_silver: useLevels ? newPromo.discountSilver : null,
                p_discount_gold: useLevels ? newPromo.discountGold : null,
                p_max_uses: maxUsesTotal,
                p_max_uses_per_user: maxUsesPerUser,
                p_end_date: newPromo.endDate,
                p_use_level_discounts: useLevels,
                p_custom_discount_percent: useLevels ? null : Math.round(Number(newPromo.customDiscountPercent)),
                p_custom_discount_name: useLevels ? null : customNameForApp,
                p_exclusive_coupon_code: exclusiveCode,
            });
            if (error) throw error;
            setShowNewPromo(false);
            setNewPromo({
                title: '',
                discountMode: 'levels',
                customDiscountName: '',
                customDiscountPercent: 10,
                customVisibility: 'public',
                exclusiveCouponCode: '',
                discountBronze: 5,
                discountSilver: 6,
                discountGold: 8,
                maxUses: null,
                maxUsesPerUser: null,
                endDate: '',
            });
            toast.success('Promoção criada com sucesso!');
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Erro ao criar promoção';
            toast.error(msg);
        }
    };

    const confirmDeletePromo = (id: string) => { setPromoToDelete(id); setDeleteModalOpen(true); };
    const handleDeletePromo = async () => {
        if (!promoToDelete || !merchant?.establishmentId) return;
        try {
            const { error } = await supabase.rpc('delete_promotion', {
                p_promotion_id: promoToDelete,
                p_establishment_id: String(merchant.establishmentId).trim(),
            });
            if (error) throw error;
            toast.success('Promoção removida com sucesso');
            setDeleteModalOpen(false);
            setPromoToDelete(null);
            setPromotionsRefreshKey((k) => k + 1);
            window.dispatchEvent(new CustomEvent(ESTABLISHMENT_DATA_CHANGED));
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Erro ao remover promoção';
            toast.error(msg);
        }
    };

    const selectTab = (tabId: MerchantTabId) => {
        setActiveTab(tabId);
        if (tabId !== 'validate') void stopScanner();
    };

    const handlePurchaseAmountChange = (value: string) => {
        validationPurchaseAmountRef.current = value;
        setValidationPurchaseAmount(value);
    };

    return (
        <div className="merchant-portal">
            <header className="merchant-app-header merchant-app-header--premium">
                <div className="merchant-header-brand">
                    <h1 className="merchant-app-logo">
                        Facilita<span className="merchant-logo-accent"> Pass+</span>
                    </h1>
                    <span className="merchant-header-subtitle">Área do parceiro</span>
                </div>
                <button
                    type="button"
                    className={'merchant-header-action ' + (activeTab === 'settings' ? 'active' : '')}
                    onClick={() => selectTab('settings')}
                    aria-label="Abrir ajustes"
                >
                    <Settings size={20} strokeWidth={activeTab === 'settings' ? 2.5 : 2} />
                </button>
            </header>

            <main className="merchant-app-main">
                <section className="merchant-hero-card merchant-hero-card--premium">
                    <div>
                        <span className="merchant-hero-pill">
                            <Store size={14} aria-hidden />
                            Sua loja
                        </span>
                        <h2 className="merchant-hero-title">{establishment?.name}</h2>
                        {establishment?.category?.label && (
                            <p className="merchant-hero-meta">{establishment.category.label}</p>
                        )}
                    </div>
                    <button type="button" className="merchant-hero-settings" onClick={() => selectTab('settings')} aria-label="Ajustar loja">
                        <Settings size={18} />
                    </button>
                </section>

                {activeTab === 'dashboard' && (
                    <MerchantDashboardTab
                        stats={stats}
                        feeSummary={feeSummary}
                        promotions={promotions}
                        onNavigate={selectTab}
                        onCreatePromotion={() => setShowNewPromo(true)}
                    />
                )}

                {activeTab === 'finance' && (
                    <MerchantFinanceTab
                        stats={stats}
                        feeSummary={feeSummary}
                        feeLoading={feeLoading}
                        recentSales={recentSales}
                        onOpenFeeDetails={() => setFeeDetailsOpen(true)}
                    />
                )}

                {activeTab === 'validate' && (
                    <MerchantValidateTab
                        validationPurchaseAmount={validationPurchaseAmount}
                        onPurchaseAmountChange={handlePurchaseAmountChange}
                        scannerActive={scannerActive}
                        onStartScanner={startScanner}
                        onStopScanner={stopScanner}
                        couponCode={couponCode}
                        onCouponCodeChange={setCouponCode}
                        onValidateCoupon={() => handleValidateCoupon()}
                        validating={validating}
                        validationError={validationError}
                        validationResult={validationResult}
                    />
                )}

                {activeTab === 'promotions' && (
                    <MerchantPromotionsTab
                        promotions={promotions}
                        promoLoading={promoLoading}
                        onCreateClick={() => setShowNewPromo(true)}
                        onDelete={confirmDeletePromo}
                    />
                )}

                {activeTab === 'media' && (
                    <MerchantMediaTab
                        coverPhoto={coverPhoto}
                        gallery={gallery}
                        video={video}
                        uploading={uploading}
                        getImageUrl={getImageUrl}
                        onFileUpload={handleFileUpload}
                        onRemoveImage={removeImage}
                        onVideoChange={setVideo}
                        onSaveMedia={handleSaveMedia}
                    />
                )}

                {activeTab === 'settings' && (
                    <MerchantSettingsTab
                        merchant={merchant}
                        establishmentNameDraft={establishmentNameDraft}
                        onEstablishmentNameChange={setEstablishmentNameDraft}
                        nameChangePassword={nameChangePassword}
                        onNameChangePasswordChange={setNameChangePassword}
                        showNameChangePassword={showNameChangePassword}
                        onToggleNamePassword={() => setShowNameChangePassword((v) => !v)}
                        updatingName={updatingName}
                        onUpdateName={handleUpdateEstablishmentName}
                        establishmentDescriptionDraft={establishmentDescriptionDraft}
                        onDescriptionChange={setEstablishmentDescriptionDraft}
                        updatingDescription={updatingDescription}
                        onUpdateDescription={handleUpdateEstablishmentDescription}
                        locationData={locationData}
                        onLocationChange={(address, latitude, longitude) => setLocationData({ address, latitude, longitude })}
                        updatingLocation={updatingLocation}
                        onUpdateLocation={handleUpdateLocation}
                        onLogout={handleLogout}
                    />
                )}
            </main>

            <nav className="merchant-bottom-nav merchant-bottom-nav--five" aria-label="Seções do portal">
                <button
                    type="button"
                    className={'merchant-nav-item ' + (activeTab === 'dashboard' ? 'active' : '')}
                    onClick={() => selectTab('dashboard')}
                >
                    <BarChart3 size={22} strokeWidth={activeTab === 'dashboard' ? 2.5 : 2} />
                    <span className="merchant-nav-label">Início</span>
                </button>
                <button
                    type="button"
                    className={'merchant-nav-item ' + (activeTab === 'finance' ? 'active' : '')}
                    onClick={() => selectTab('finance')}
                >
                    <Wallet size={22} strokeWidth={activeTab === 'finance' ? 2.5 : 2} />
                    <span className="merchant-nav-label">Financeiro</span>
                </button>
                <button
                    type="button"
                    className={'merchant-nav-item merchant-nav-item--primary ' + (activeTab === 'validate' ? 'active' : '')}
                    onClick={() => selectTab('validate')}
                >
                    <QrCode size={24} strokeWidth={activeTab === 'validate' ? 2.7 : 2.2} />
                    <span className="merchant-nav-label">Validar</span>
                </button>
                <button
                    type="button"
                    className={'merchant-nav-item ' + (activeTab === 'promotions' ? 'active' : '')}
                    onClick={() => selectTab('promotions')}
                >
                    <Tag size={22} strokeWidth={activeTab === 'promotions' ? 2.5 : 2} />
                    <span className="merchant-nav-label">Promos</span>
                </button>
                <button
                    type="button"
                    className={'merchant-nav-item ' + (activeTab === 'media' ? 'active' : '')}
                    onClick={() => selectTab('media')}
                >
                    <Image size={22} strokeWidth={activeTab === 'media' ? 2.5 : 2} />
                    <span className="merchant-nav-label">Mídia</span>
                </button>
            </nav>

            <MerchantPromotionModal
                isOpen={showNewPromo}
                promotion={newPromo}
                onChange={setNewPromo}
                onClose={() => setShowNewPromo(false)}
                onSubmit={handleCreatePromo}
            />

            <Modal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} title="Excluir promoção"
                footer={<>
                    <Button variant="ghost" onClick={() => setDeleteModalOpen(false)}>Cancelar</Button>
                    <Button variant="primary" onClick={handleDeletePromo} style={{ backgroundColor: '#EF4444', color: 'white' }}>Excluir</Button>
                </>}>
                <p>Tem certeza que deseja excluir esta promoção? Esta ação não pode ser desfeita.</p>
            </Modal>

            <MerchantPlatformFeeDetailsModal
                isOpen={feeDetailsOpen}
                stats={stats}
                feeSummary={feeSummary}
                platformPayments={platformPayments}
                recentSales={recentSales}
                feeLoading={feeLoading}
                creatingPayment={creatingPayment}
                onClose={() => setFeeDetailsOpen(false)}
                onGeneratePix={handlePayPlatformFee}
            />

            <MerchantPixQrModal
                isOpen={pixModalOpen}
                pixPayment={pixPayment}
                pixCopied={pixCopied}
                onClose={() => setPixModalOpen(false)}
                onCopy={copyPixCode}
            />
        </div>
    );
};

export default MerchantPortal;
